import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { tvApi } from "../api/tvApi.jsx";
import useTVStore from "../store/tvStore.jsx";

// 쿼리 키 상수
export const TV_QUERY_KEYS = {
  all: ["tvs"],
  lists: () => [...TV_QUERY_KEYS.all, "list"],
  list: (filters) => [...TV_QUERY_KEYS.lists(), { filters }],
  details: () => [...TV_QUERY_KEYS.all, "detail"],
  detail: (id) => [...TV_QUERY_KEYS.details(), id],
  auth: (id) => [...TV_QUERY_KEYS.detail(id), "auth"],
  // 🆕 TV 상태 조회용 키 추가
  status: (id) => [...TV_QUERY_KEYS.detail(id), "status"],
  statusByName: (name) => [...TV_QUERY_KEYS.all, "status", "name", name],
};

// TV 목록 조회
export const useTVs = () => {
  const setTVs = useTVStore((state) => state.setTVs);

  return useQuery({
    queryKey: TV_QUERY_KEYS.lists(),
    queryFn: async () => {
      const response = await tvApi.getTvList();
      return response.success ? response.data : [];
    },
    onSuccess: (data) => {
      setTVs(data);
    },
    staleTime: 5 * 60 * 1000, // 5분간 fresh
    cacheTime: 10 * 60 * 1000, // 10분간 캐시 유지
  });
};

// TV 상세 조회
export const useTV = (tvId) => {
  const setCurrentTV = useTVStore((state) => state.setCurrentTV);

  return useQuery({
    queryKey: TV_QUERY_KEYS.detail(tvId),
    queryFn: async () => {
      const response = await tvApi.getTv(tvId);
      return response.success ? response.data : null;
    },
    enabled: !!tvId,
    onSuccess: (data) => {
      if (data) {
        setCurrentTV(data);
      }
    },
    staleTime: 2 * 60 * 1000, // 2분간 fresh
  });
};

// TV 재생용 상세 조회 (TV name으로 조회)
export const useTVPlay = (tvName) => {
  const setCurrentTV = useTVStore((state) => state.setCurrentTV);

  return useQuery({
    queryKey: [...TV_QUERY_KEYS.all, "play", tvName],
    queryFn: async () => {
      const response = await tvApi.getTvPlay(tvName);
      return response.success ? response.data : null;
    },
    enabled: !!tvName,
    onSuccess: (data) => {
      if (data) {
        setCurrentTV(data);
      }
    },
    staleTime: 2 * 60 * 1000, // 2분간 fresh
  });
};

// 🆕 TV 상태만 조회 (updatedAt, enabled, exists 확인용)
export const useTVStatus = (tvId, options = {}) => {
  return useQuery({
    queryKey: TV_QUERY_KEYS.status(tvId),
    queryFn: async () => {
      const response = await tvApi.getTvStatus(tvId);
      return response.success ? response.data : null;
    },
    enabled: !!tvId && options.enabled !== false,
    staleTime: 30 * 1000, // 30초간 fresh (짧게 설정)
    cacheTime: 2 * 60 * 1000, // 2분간 캐시
    retry: 1, // 한 번만 재시도
    refetchOnWindowFocus: false, // 윈도우 포커스 시 자동 refetch 비활성화
    ...options, // 추가 옵션 허용
  });
};

// 🆕 TV name으로 상태 조회
export const useTVStatusByName = (tvName, options = {}) => {
  return useQuery({
    queryKey: TV_QUERY_KEYS.statusByName(tvName),
    queryFn: async () => {
      const response = await tvApi.getTvStatusByName(tvName);
      return response.success ? response.data : null;
    },
    enabled: !!tvName && options.enabled !== false,
    staleTime: 30 * 1000, // 30초간 fresh
    cacheTime: 2 * 60 * 1000, // 2분간 캐시
    retry: 1,
    refetchOnWindowFocus: false,
    ...options,
  });
};

// TV 인증 여부 조회
export const useTVAuthentication = (tvId) => {
  return useQuery({
    queryKey: TV_QUERY_KEYS.auth(tvId),
    queryFn: async () => {
      const response = await tvApi.getTvAuthentication(tvId);
      return response.success ? response.data : false;
    },
    enabled: !!tvId,
    staleTime: 1 * 60 * 1000, // 1분간 fresh
    retry: 1,
  });
};

// TV 생성
export const useCreateTV = () => {
  const queryClient = useQueryClient();
  const addTV = useTVStore((state) => state.addTV);

  return useMutation({
    mutationFn: async (tvData) => {
      const response = await tvApi.createTv(tvData);
      return response.success ? response.data : null;
    },
    onSuccess: (data) => {
      if (data) {
        // 캐시 무효화
        queryClient.invalidateQueries({ queryKey: TV_QUERY_KEYS.lists() });
        // 로컬 상태 업데이트
        addTV(data);
      }
    },
    onError: (error) => {
      // TV 생성 실패 처리
    },
  });
};

// 🎯 TV 수정 - 전체 설정 저장용 (AdminPanel에서 사용)
export const useUpdateTV = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ tvId, tvData }) => {


      // tvApi.updateTv 사용 (백엔드 TvUpdateRequest 스펙에 맞는 데이터 전송)
      const response = await tvApi.updateTv(tvId, tvData);

      if (!response.success) {
        throw new Error(response.message || "TV 수정에 실패했습니다.");
      }

      return response.data;
    },
    onSuccess: (data, variables) => {


      // 관련 캐시 무효화
      queryClient.invalidateQueries({ queryKey: TV_QUERY_KEYS.lists() });
      queryClient.invalidateQueries({
        queryKey: TV_QUERY_KEYS.detail(variables.tvId),
      });
      // 🆕 상태 캐시도 무효화
      queryClient.invalidateQueries({
        queryKey: TV_QUERY_KEYS.status(variables.tvId),
      });
    },
    onError: (error) => {
      // 에러를 다시 throw하여 UI에서 처리할 수 있도록 함
      throw error;
    },
  });
};

// TV 활성화/비활성화
export const useUpdateTVEnable = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ tvId, enabled }) => {
      const response = await tvApi.updateTvEnable(tvId, enabled);
      return response.success ? response.data : null;
    },
    onSuccess: (data, variables) => {
      if (data) {
        // 관련 캐시 무효화
        queryClient.invalidateQueries({ queryKey: TV_QUERY_KEYS.lists() });
        queryClient.invalidateQueries({
          queryKey: TV_QUERY_KEYS.detail(variables.tvId),
        });
        // 🆕 상태 캐시도 무효화
        queryClient.invalidateQueries({
          queryKey: TV_QUERY_KEYS.status(variables.tvId),
        });
      }
    },
    onError: (error) => {
      // TV 활성화/비활성화 실패 처리
    },
  });
};

// TV 다중 삭제
export const useDeleteTVMulti = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tvIds) => {
      const response = await tvApi.deleteTvMulti(tvIds);
      return response.success ? response.data : null;
    },
    onSuccess: (_, tvIds) => {
      // 캐시 무효화
      queryClient.invalidateQueries({ queryKey: TV_QUERY_KEYS.lists() });

      // 🆕 삭제된 TV들의 개별 캐시도 무효화
      tvIds.forEach((tvId) => {
        queryClient.invalidateQueries({ queryKey: TV_QUERY_KEYS.detail(tvId) });
        queryClient.invalidateQueries({ queryKey: TV_QUERY_KEYS.status(tvId) });
      });
    },
    onError: (error) => {
      // TV 삭제 실패 처리
    },
  });
};

// 🆕 수동으로 TV 상태를 확인하는 유틸리티 훅
export const useManualTVStatusCheck = () => {
  const queryClient = useQueryClient();

  return {
    checkStatus: async (tvId) => {
      try {
        // 강제로 캐시를 무시하고 새로운 데이터 조회
        const data = await queryClient.fetchQuery({
          queryKey: TV_QUERY_KEYS.status(tvId),
          queryFn: async () => {
            const response = await tvApi.getTvStatus(tvId);
            return response.success ? response.data : null;
          },
          staleTime: 0, // 즉시 stale로 처리
        });
        return data;
      } catch (error) {
        return null;
      }
    },

    checkStatusByName: async (tvName) => {
      try {
        const data = await queryClient.fetchQuery({
          queryKey: TV_QUERY_KEYS.statusByName(tvName),
          queryFn: async () => {
            const response = await tvApi.getTvStatusByName(tvName);
            return response.success ? response.data : null;
          },
          staleTime: 0,
        });
        return data;
      } catch (error) {
        return null;
      }
    },
  };
};
